import { getCustomThemeOverrides, getThemeCssVariables } from '@getjust/leaf';
import { useEffect, useMemo } from 'react';

import { useShop } from '$hooks/queries/useShop';
import { useROBrandingPreviewAtom } from '$hooks/state/useBrandingPreviewAtom';
import { getEffectiveCheckoutTheme, getThemeColors } from '$src/utils/themeColors';

/**
 * Component that applies the checkout theme based on shop settings.
 * This component should be rendered inside InitWrapper after shop data is available.
 * It applies CSS variables and data attributes to the document for theme-specific styling.
 *
 * When in branding preview mode (partner dashboard iframe), overrides from postMessage
 * take priority over shop settings.
 */
export function ThemeApplicator() {
  const { data: shop } = useShop();
  const brandingPreview = useROBrandingPreviewAtom();

  const themeName = useMemo(
    () => (brandingPreview ? brandingPreview.checkoutTheme : getEffectiveCheckoutTheme(shop)),
    [shop?.settings?.checkoutTheme, brandingPreview],
  );
  const brandStyles = brandingPreview?.brandStyles ?? shop?.settings?.brandStyles;

  const cssVariables = useMemo(() => getThemeCssVariables(themeName), [themeName]);
  const customOverrides = useMemo(
    () => (themeName === 'custom' && brandStyles ? getCustomThemeOverrides(brandStyles) : null),
    [themeName, brandStyles],
  );
  const colors = useMemo(() => getThemeColors(themeName), [themeName]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    const body = document.body;

    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    if (customOverrides) {
      Object.entries(customOverrides).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    }

    body.setAttribute('data-theme', themeName);
    body.style.backgroundColor = customOverrides?.['--bg'] ?? colors.bg;
    body.style.color = colors.text;

    return () => {
      Object.keys(cssVariables).forEach((key) => {
        root.style.removeProperty(key);
      });
      if (customOverrides) {
        Object.keys(customOverrides).forEach((key) => {
          root.style.removeProperty(key);
        });
      }
      body.removeAttribute('data-theme');
      body.style.backgroundColor = '';
      body.style.color = '';
    };
  }, [themeName, cssVariables, customOverrides]);

  return null;
}
